/**
 * Copyright (c) Microsoft. All rights reserved.
 */

// tslint:disable:strict-boolean-expressions

import { CLIENT_VERSION } from './Constants';
import {
    Client, ConnectionInfo, IConnectionInfo,
    ITrouterServiceBase, ITrouterServiceConfig, Listener, LogProvider, MessageHandler,
    Request, Response, StateChangedListener,
    TrouterConnectionListener, TrouterRequestBody, TrouterState, TrouterTelemetrySettings,
    UserActivityState
} from './Interfaces';
import { ITrouterOptions, ServerState } from './InternalInterfaces';
import { Logger } from './Logger';
import { MessageHandlerRegistry } from './MessageHandler';
import { addCacheAsBackupTo } from './SkypeTokenProviderWithCache';
import { TrouterManager } from './TrouterManager';
import { TrouterUrlPromise } from './TrouterUrlPromise';

export { MessageHandler, TrouterState, UserActivityState };

interface IEcsConfig {
    TrouterConnectionUrl?: string;
    TelemetryEnabled?: boolean;
    ClientTelemetryEventEnabled: {
        trouter_js_client_connected?: boolean;
        trouter_js_client_disconnected?: boolean;
        trouter_js_client_error?: boolean;
        trouter_js_client_progress?: boolean;
        trouter_js_client_response?: boolean;
        logHealthCheckError?: boolean;
        logSendPingError?: boolean;
        numberOfStepsToMaintain?: number;
        sendProgressTimeoutSecs?: number;
    };
}

function populateTelemetryOptions(startingCfg: TrouterTelemetrySettings, ecsCfg?: IEcsConfig): TrouterTelemetrySettings {
    if (!ecsCfg) {
        return startingCfg;
    }

    let res = {
        ...startingCfg,
        ...{
            enabled: ecsCfg.TelemetryEnabled
        }
    };

    if (ecsCfg.ClientTelemetryEventEnabled !== undefined) {
        res = {
            ...res,
            ...ecsCfg.ClientTelemetryEventEnabled
        };
    }

    return <TrouterTelemetrySettings>res;
}

function populateTrouterOptions(cfg: ITrouterServiceConfig, ecsCfg?: IEcsConfig): ITrouterOptions {
    return {
        clientInfo: {
            ua: cfg.trouterSettings.productName,
            v: cfg.trouterSettings.version
        },
        ioOptions: {
            // uncomment for manual testing
            // transports: ['xhr-polling'],
            // 'connect timeout': XXX, 10000 by default in socket.io code
            ackTimeoutMs: 5000
        },
        clientCorrelationID: cfg.trouterSettings.sessionId,
        environment: cfg.trouterSettings.environment,
        telemetrySettings: populateTelemetryOptions(cfg.telemetryConfig.settings, ecsCfg),
        eventLogger: cfg.telemetryConfig.eventLogger,

        endpointId: cfg.trouterSettings.registrationId,
        trouterUrl: (ecsCfg && ecsCfg.TrouterConnectionUrl) || cfg.trouterSettings.trouterServiceUrl,
        registration: cfg.trouterSettings.registrarServiceUrl
            ? {
                registrarUrl: cfg.trouterSettings.registrarServiceUrl,
                registrationId: cfg.trouterSettings.registrationId || '',

                pnhAppId: cfg.trouterSettings.pnhAppId || '',
                platform: cfg.trouterSettings.platform || '',
                pnhTemplateKey: cfg.trouterSettings.pnhTemplate || '',
                platformUIVersion: cfg.trouterSettings.platformUIVersion || '',
                productContext: cfg.trouterSettings.pnhProductContext || undefined,
                context: cfg.trouterSettings.pnhContext || '',

                registrarTtlSec: (cfg.trouterSettings.maxRegistrationTimeInMs || 0) / 1000
            }
            : undefined,

        timeoutOptions: {
            connectionTimeoutMs: cfg.trouterSettings.trouterConnectTimeoutInMs || 30000,
            fetchTimeoutMs: 10000,
            pingTimeoutMs: 40000,
            pongTimeoutMs: 5000,
            /** Followup WI to remove hardcoded value: https://domoreexp.visualstudio.com/MSTeams/_workitems/edit/2307271 */
            maxBackoffMs: cfg.trouterSettings.productName === 'TeamsCDL' ? 300000 : 30000,
            requestTimeoutMs: 5000,
            userActivityResponseTimeoutMs: 10000,
            ...cfg.trouterSettings.timeoutOptions
        },
        incallTimeoutOptions: {
            connectionTimeoutMs: 10000,
            fetchTimeoutMs: 5000,
            pingTimeoutMs: 5000,
            pongTimeoutMs: 2000,
            maxBackoffMs: cfg.trouterSettings.productName === 'TeamsCDL' ? 300000 : 10000,
            requestTimeoutMs: 5000,
            userActivityResponseTimeoutMs: 10000,
            ...cfg.trouterSettings.incallTimeoutOptions
        },
        incallModeTimeoutMs: cfg.trouterSettings.incallModeTimeoutMs || 0,
        lingeringConnectionDelayMs: 60000,
        userActivitySecondResendDelayMs: cfg.trouterSettings.userActivitySecondResendDelayMs || 10000,
        duplicateDisconnectThresholdMs: 10000,
        connectionCache: cfg.connectionCache,
        registrationStateCallback: cfg.registrationStateCallbackForAcsDoNotUse,
        retryLimitOnTokenFetch: cfg.trouterSettings.productName === 'TeamsCDL' ? 15 : cfg.trouterSettings.retryLimitOnTokenFetch
    };
}

export type TelemetryEvent = {
    // tslint:disable-next-line:no-reserved-keywords
    type: string;
    registrationId: string;
    connectionId?: string;
    connectedClientId?: string;
};

interface IWrappedStateChangedListener extends TrouterConnectionListener {
    (state: TrouterState, info?: IConnectionInfo): void;
    wrappedCallback: StateChangedListener;
}

/**
 * TrouterService implementation class
 */
export class TrouterService implements ITrouterServiceBase, Client, Listener {
    private logger: Logger;
    private trouterServer?: TrouterManager;
    private listeners: { [path: string]: Listener };
    private connectionInfo: ConnectionInfo | null;
    private trouterCfg?: ITrouterServiceConfig;
    private ecsCfg?: IEcsConfig;


    // Legacy fields
    private trouterUrlPromise: TrouterUrlPromise;
    private stateChangedListeners: (TrouterConnectionListener | IWrappedStateChangedListener)[] = [];
    private messageHandlers: MessageHandlerRegistry;

    public constructor(private logProvider: LogProvider) {
        this.logger = new Logger('Trouter', logProvider);
        this.trouterUrlPromise = new TrouterUrlPromise(logProvider);
        this.messageHandlers = new MessageHandlerRegistry(logProvider);
        this.listeners = {};
        this.connectionInfo = null;
        this.logger.info(`Created TrouterService version ${CLIENT_VERSION}`);
    }

    public start(cfg: ITrouterServiceConfig): void {
        this.logger.info('Start');

        if (!cfg.trouterSettings.disableInternalSkypeTokenCache) {
            const provider = cfg.skypeTokenProvider;
            cfg.skypeTokenProvider = addCacheAsBackupTo(provider);
        }

        this.trouterCfg = cfg;
        const options = populateTrouterOptions(cfg, this.ecsCfg);
        if (this.trouterServer === undefined) {
            this.trouterServer = new TrouterManager(
                this.logProvider,
                options,
                cfg.skypeTokenProvider,
                this);
        }
        this.trouterServer.start();
    }

    public stop(dontUnregister?: boolean): void {
        this.logger.info('close connection');
        this.trouterUrlPromise.rejectUrl(new Error('TrouterService is stopped'));

        if (this.trouterServer !== undefined) {
            this.trouterServer.stop(dontUnregister);
        }
    }

    public async setEcsConfig(fullEcsData: {}): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const ecsSection = 'TrouterJScriptClient';
            this.ecsCfg = <IEcsConfig>(fullEcsData[ecsSection]);
            this.logger.info(`Setting ECS configuration to ${JSON.stringify(this.ecsCfg)}`);
            if (this.trouterServer !== undefined && this.trouterCfg !== undefined) {
                const options = populateTrouterOptions(this.trouterCfg, this.ecsCfg);
                this.trouterServer.configure(options);
            }
            resolve();
        });
    }

    /**
     * Notifies trouter that network is restored - trouter will try to restore connection immediately
     */
    public checkConnection(disconnectDetected?: boolean): void {
        if (this.trouterServer !== undefined) {
            this.trouterServer.checkConnection(disconnectDetected || false);
        }
    }

    public async resendRegistration(): Promise<void> {
        if (!this.trouterServer) {
            throw new Error('resendRegistration called too early');
        }

        return this.trouterServer.resendRegistration();
    }


    // Listeners

    public registerListener(listener: Listener, path: string): boolean {
        if (path === '' || path[0] !== '/' || path.indexOf('?') >= 0 || path.indexOf('#') >= 0) {
            this.logger.error(`Listener path '${path}' is not valid`);

            return false;
        }
        if (this.listeners[path]) {
            this.logger.error(`Another listener is already registered for path '${path}'`);

            return false;
        }
        this.listeners[path] = listener;
        this.logger.debug(`Listener for path '${path}' registered`);
        if (this.connectionInfo) {
            listener.onTrouterConnected(this.connectionInfo.baseEndpointUrl + path, this.connectionInfo);
        }

        return true;
    }

    public unregisterListener(listener: Listener): boolean {
        const paths: string[] = [];
        for (const path of Object.keys(this.listeners)) {
            if (this.listeners[path] === listener) {
                paths.push(path);
            }
        }
        if (paths.length === 0) {
            return false;
        }
        for (const path of paths) {
            delete this.listeners[path];
        }
        this.logger.debug(`Listener for path(s) '${paths.join('\', \'')}' unregistered`);

        return true;
    }

    public onTrouterConnected(endpointUrl: string, connectionInfo: ConnectionInfo): void {
        this.logger.debug('Trouter is now connected');
        for (const path of Object.keys(this.listeners)) {
            try {
                this.listeners[path].onTrouterConnected(connectionInfo.baseEndpointUrl + path, connectionInfo);
            } catch (e) {
                this.logger.error(`Listener '${path}' threw an exception from onTrouterConnected(): ${e}`);
            }
        }
        this.connectionInfo = connectionInfo;

        this.trouterUrlPromise.resolveUrl(endpointUrl);
        this.notifyStateChanged(TrouterState.Connected, {
            url: endpointUrl,
            getRemainingTtlInSec: () => connectionInfo.connectionTtlSec
        });
    }

    public onTrouterDisconnected(): void {
        this.logger.debug('Trouter is now disconnected');
        this.connectionInfo = null;
        for (const path of Object.keys(this.listeners)) {
            const listener = this.listeners[path];
            if (listener.onTrouterDisconnected) {
                try {
                    listener.onTrouterDisconnected();
                } catch (e) {
                    this.logger.error(`Listener '${path}' threw an exception from onTrouterDisconnected(): ${e}`);
                }
            }
        }

        this.notifyStateChanged(TrouterState.Disconnected);
    }

    public onTrouterRequest(request: Request, response: Response): void {
        let bestPath = '';
        for (const path of Object.keys(this.listeners)) {
            if (request.path.substring(0, path.length) === path && path.length > bestPath.length) {
                bestPath = path;
            }
        }
        if (bestPath !== '') {
            try {
                this.listeners[bestPath].onTrouterRequest(request, response);
            } catch (e) {
                this.logger.error(`Listener '${bestPath}' threw an exception from onTrouterRequest(): ${e}`);
                response.status = 500;
                response.headers = { 'Trouter-Responder': 'ClientLib' };
                response.send();
            }

            return;
        }

        if (this.tryMessageHandlers(request, response)) {
            return;
        }

        response.status = 404;
        response.headers = { 'Trouter-Responder': 'ClientLib' };
        response.send();
    }

    public onTrouterMessageLoss(flowTags: string[]): boolean {
        this.logger.info(`onTrouterMessageLoss called with value ${flowTags}`);

        let messageLossProcessed = true;
        for (const path of Object.keys(this.listeners)) {
            const listener = this.listeners[path];
            if (listener.onTrouterMessageLoss) {
                try {
                    messageLossProcessed = listener.onTrouterMessageLoss(flowTags) && messageLossProcessed;

                    if (messageLossProcessed === undefined) {
                        this.logger.error(`Listener '${path}' did not response with boolean value onTrouterMessageLoss()`);
                        messageLossProcessed = false;
                    }
                } catch (e) {
                    this.logger.error(`Listener '${path}' threw an exception from onTrouterMessageLoss(): ${e}`);
                    messageLossProcessed = false;
                }
            }
        }

        return messageLossProcessed;
    }

    public onTrouterUserActivityStateAccepted(cv: string): void {
        this.logger.debug(`onTrouterUserActivityStateAccepted cv: ${cv}`);
        for (const path of Object.keys(this.listeners)) {
            const listener = this.listeners[path];
            if (listener.onTrouterUserActivityStateAccepted) {
                try {
                    listener.onTrouterUserActivityStateAccepted(cv);
                } catch (e) {
                    this.logger.error(`Listener '${path}' threw an exception from onTrouterUserActivityStateAccepted(): ${e}`);
                }
            }
        }
    }

    public setUserActivityState(state: UserActivityState, correlationVector?: string): void {
        if (state !== UserActivityState.Active && state !== UserActivityState.Inactive) {
            throw new Error(`setUserActivityState called with unsupported value ${state}`);
        }
        this.logger.info(`setUserActivityState called with value ${UserActivityState[state]}`);

        if (this.trouterServer && this.state() !== TrouterState.Unknown) {
            this.trouterServer.setUserActivityState(state, correlationVector);
        } else {
            throw new Error('setUserActivityState called too early');
        }
    }


    // Legacy API

    public state(): TrouterState {
        if (this.trouterServer !== undefined) {
            return this.trouterServer.getState();
        }

        return TrouterState.Unknown;
    }

    public getServerState(): ServerState | undefined {
        if (this.trouterServer !== undefined) {
            return this.trouterServer.getServerState();
        }

        return undefined;
    }

    // tslint:disable-next-line:promise-function-async
    public getTrouterUrlAsync(): Promise<string> {
        if (this.trouterServer !== undefined) {
            return this.trouterUrlPromise.getPromise();
        } else {
            return Promise.reject(new Error('TrouterService has not been started'));
        }
    }

    public onStateChanged(callback: StateChangedListener | undefined): void {
        this.logger.info('onStateChanged called');
        if (callback === undefined) {
            this.stateChangedListeners = this.stateChangedListeners.filter(
                (listener) => ((<IWrappedStateChangedListener>listener).wrappedCallback === undefined));
        } else {
            this.offStateChanged(callback);
            const wrapper = (state: TrouterState, info?: IConnectionInfo) => {
                callback(state, info ? info.url : '');
            };
            (<IWrappedStateChangedListener>wrapper).wrappedCallback = callback;
            this.stateChangedListeners.push(wrapper);
        }
    }

    public offStateChanged(callback: StateChangedListener): boolean {
        this.logger.info('offStateChanged called');
        const sizeBeforeRemoval = this.stateChangedListeners.length;
        this.stateChangedListeners = this.stateChangedListeners.filter(
            (listener) => ((<IWrappedStateChangedListener>listener).wrappedCallback !== callback));

        return (sizeBeforeRemoval > this.stateChangedListeners.length);
    }

    public addCallback(callback: TrouterConnectionListener): void {
        this.logger.info('addListener called');
        const index = this.stateChangedListeners.indexOf(callback, 0);
        if (index === -1 && callback !== undefined) {
            this.stateChangedListeners.push(callback);
        }
    }

    public removeCallback(callback: TrouterConnectionListener): boolean {
        this.logger.info('removeListener called');
        const index = this.stateChangedListeners.indexOf(callback, 0);
        if (index > -1) {
            this.stateChangedListeners.splice(index, 1);

            return true;
        }

        return false;
    }

    public registerMessageHandler(handler: MessageHandler) {
        this.logger.info('registerMessageHandler is called');
        this.messageHandlers.register(handler);
    }

    public clearMessageHandlers(): void {
        this.logger.info('clearMessageHandlers is called');
        this.messageHandlers.clear();
    }

    private notifyStateChanged(state: TrouterState, info?: IConnectionInfo): void {
        this.logger.info(`notifyStateChanged called, will forward to ${this.stateChangedListeners.length} listeners`);
        this.stateChangedListeners.forEach(callback => {
            try {
                callback(state, info);
            } catch (error) {
                this.logger.error(`Error in callback ${error}`);
            }
        });
    }

    private tryMessageHandlers(request: Request, response: Response): boolean {
        if (!this.messageHandlers.active()) {

            return false;
        }

        let body: TrouterRequestBody | undefined;
        let eventId: number | null = null;

        try {
            body = <TrouterRequestBody>JSON.parse(request.body);
            eventId = (body && (body.evt || body.eventId)) || null;
        } catch (e) { }

        const message = {
            eventId: eventId,
            url: (this.connectionInfo ? this.connectionInfo.baseEndpointUrl : '') + request.path,
            body: body,
            rawBody: request.body,
            headers: request.headers
        };

        const result = this.messageHandlers.handleMessage(message);
        if (!result.isHandled) {

            return false;
        }

        response.status = result.resultCode;
        if (result.responseHeaders) {
            response.headers = result.responseHeaders;
        }
        if (result.responseBody) {
            response.body = result.responseBody;
        }
        response.send();

        return true;
    }
}

export function createTrouterService(logger: LogProvider): ITrouterServiceBase {
    return new TrouterService(logger);
}

export function getTrouterServiceVersion(): string {
    return CLIENT_VERSION;
}

export function replaceTrouterUrlBase(existingUrl: string, newBase: string): string {
    const schemaSepPos = existingUrl.indexOf('://');
    if (schemaSepPos >= 0) {
        const pathSepPos = existingUrl.indexOf('/', schemaSepPos + 3);
        if (pathSepPos >= 0) {
            return newBase + existingUrl.substr(pathSepPos);
        }
    }

    return '';
}



// WEBPACK FOOTER //
// ./lib_temp/TsTrouter.ts